import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldAlert, Check, X, LogOut } from "lucide-react";

const RULES = [
  { test: (v) => v.length >= 8, label: "At least 8 characters" },
  { test: (v) => /[A-Z]/.test(v), label: "One uppercase letter" },
  { test: (v) => /[a-z]/.test(v), label: "One lowercase letter" },
  { test: (v) => /\d/.test(v), label: "One number" },
  { test: (v) => /[!@#$%^&*(),.?":{}|<>_\-+=[\]/\\;'`~]/.test(v), label: "One special character" },
];

export default function ForceChangePassword() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to="/" replace />;

  const allPass = RULES.every((r) => r.test(newPwd));
  const match = newPwd && newPwd === confirmPwd;
  const ready = allPass && match && oldPwd.length > 0 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      if (data?.token) localStorage.setItem("qie_token", data.token);
      toast.success("Password changed successfully");
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    }
    setBusy(false);
  };

  const onLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#F8FAFC] bg-grain grid place-items-center px-4 py-12" data-testid="force-change-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 animate-fade-up">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-md bg-[#D97706]/10 grid place-items-center shrink-0">
              <ShieldAlert className="w-6 h-6 text-[#D97706]" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D97706] font-semibold">Action Required</div>
              <h1 className="font-heading text-2xl font-bold text-[#0F172A] mt-1">Change Your Password</h1>
              <p className="text-sm text-[#475569] mt-1">For security, you must change your default password before you can continue.</p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-[#475569]">Current / Temporary Password *</Label>
              <Input
                type="password"
                data-testid="fc-old-password"
                value={oldPwd}
                onChange={(e) => setOldPwd(e.target.value)}
                required
                className="mt-1.5 h-11"
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#475569]">New Password *</Label>
              <Input
                type="password"
                data-testid="fc-new-password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                required
                className="mt-1.5 h-11"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#475569]">Confirm New Password *</Label>
              <Input
                type="password"
                data-testid="fc-confirm-password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
                className="mt-1.5 h-11"
                autoComplete="new-password"
              />
            </div>

            {/* Policy checklist */}
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569] mb-2">Password Requirements</div>
              <ul className="space-y-1.5">
                {RULES.map((r) => {
                  const ok = r.test(newPwd);
                  return (
                    <li key={r.label} className="flex items-center gap-2 text-xs">
                      {ok ? <Check className="w-3.5 h-3.5 text-[#059669]" /> : <X className="w-3.5 h-3.5 text-[#94A3B8]" />}
                      <span className={ok ? "text-[#0F172A]" : "text-[#64748B]"}>{r.label}</span>
                    </li>
                  );
                })}
                <li className="flex items-center gap-2 text-xs">
                  {match ? <Check className="w-3.5 h-3.5 text-[#059669]" /> : <X className="w-3.5 h-3.5 text-[#94A3B8]" />}
                  <span className={match ? "text-[#0F172A]" : "text-[#64748B]"}>Passwords match</span>
                </li>
              </ul>
            </div>

            <Button
              type="submit"
              disabled={!ready}
              data-testid="fc-submit"
              className="w-full h-11 bg-[#0F2E4A] hover:bg-[#174169] text-white disabled:opacity-40"
            >
              {busy ? "Updating..." : "Update Password & Continue"}
            </Button>
            <button
              type="button"
              onClick={onLogout}
              data-testid="fc-logout"
              className="w-full text-xs text-[#64748B] hover:text-[#DC2626] py-1 inline-flex items-center justify-center gap-1.5 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out instead
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
